import React, { useEffect } from 'react';
import { useApolloClient } from '@apollo/client';
import {
  CalendarQueryVariables,
  FlightOptionsQueryVariables,
  Offer,
  OffersQueryVariables,
  useCalendarQuery,
  useOffersQuery,
} from '@gql/schema';
import * as Sentry from '@sentry/react';
import { useTranslations } from '@utils/useTranslations';
import {
  IFlightOffer,
  IFlightState,
  TWithFlightsOffers,
  getAirportOptions,
  getNightOptions,
} from '@features/Flights';
import { fetchFlightOptions } from './AvailabilityPresentation.queries';
import { getCheapestFlightOptions, notEmpty } from './AvailabilityPresentation.utils';

interface Props {
  queryVariables: CalendarQueryVariables;
  withFlightsOffers: TWithFlightsOffers;
  flightsSelected: boolean;
  onFlightStateChange: React.Dispatch<React.SetStateAction<IFlightState>>;
}

export const useData = ({
  queryVariables,
  withFlightsOffers,
  flightsSelected,
  onFlightStateChange,
}: Props) => {
  const client = useApolloClient();
  const { translations } = useTranslations();

  const offersQueryVariables: OffersQueryVariables = {
    saleId: queryVariables.saleId,
    affiliate: queryVariables.affiliate,
    territory: queryVariables.territory,
  };

  const {
    data: offersData,
    error: offersError,
    refetch: offersRefetch,
    loading: offersLoading,
  } = useOffersQuery({
    variables: offersQueryVariables,
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    onError: error => {
      if (error.networkError) {
        Sentry.captureException(error.networkError);
      }
      error.graphQLErrors.forEach(gqError => Sentry.captureException(gqError));
    },
  });

  const {
    data: availabilityData,
    error: availabilityError,
    refetch: availabilityRefetch,
    loading: availabiltyLoading,
  } = useCalendarQuery({
    variables: queryVariables,
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    onError: error => {
      if (error.networkError) {
        Sentry.captureException(error.networkError);
      }
      error.graphQLErrors.forEach(gqError => Sentry.captureException(gqError));
    },
  });

  // Fetch flight options if tripType is H+ on first load
  useEffect(() => {
    const fetchOptions = async (fetchedOffers: Offer[]) => {
      const variables: FlightOptionsQueryVariables = {
        saleId: queryVariables.saleId,
        affiliate: queryVariables.affiliate,
        territory: queryVariables.territory,
        flightsEnabled: true,
        departureAirportCode: queryVariables.departureAirports?.[0],
        duration: Array.isArray(queryVariables.nights)
          ? queryVariables.nights?.[0]
          : queryVariables.nights,
      };

      const queries = fetchedOffers.map(offer =>
        fetchFlightOptions(client, {
          ...variables,
          offerId: offer?.id,
        })
      );

      const queryResults = await Promise.all(queries);

      const { flightOffers, airports, durations } = getCheapestFlightOptions(queryResults);

      onFlightStateChange(current => ({
        ...current,
        airports: getAirportOptions(airports, translations),
        nights: getNightOptions(durations, translations),
        offers: flightOffers as IFlightOffer[],
      }));
    };

    if (offersData && flightsSelected) {
      void fetchOptions(offersData?.sale?.offers?.filter(notEmpty) ?? []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offersData]);

  const transformedOffers = React.useMemo(
    () =>
      offersData?.sale?.offers
        ?.filter(notEmpty)
        .filter(offer =>
          flightsSelected && withFlightsOffers ? offer.id in withFlightsOffers : !!offer.prices
        ) ?? ([] as Offer[]),
    [offersData, flightsSelected, withFlightsOffers]
  );

  const errors = [
    ...(offersError ? [offersError] : []),
    ...(availabilityError ? [availabilityError] : []),
  ];

  return {
    availability: {
      data: availabilityData?.sale?.calendar,
      loading: availabiltyLoading,
    },
    offers: {
      data: transformedOffers,
      loading: offersLoading,
    },
    errors,
    refetch: async () => {
      await Promise.all([offersRefetch(), availabilityRefetch()]);
    },
    availabilityRefetch,
  };
};
